'use client';

import { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

interface FAQ {
  id: number;
  category: string;
  question: string;
  answer: string;
}

interface FAQContentProps {
  faqs: FAQ[];
  eyebrow?: string;
  title?: string;
  description?: string;
}

export function FAQContent({
  faqs,
  eyebrow = 'FAQ',
  title = 'Frequently Asked Questions',
  description = 'Find answers to common questions about training, parts, cooperation and technical support. Still have questions? Feel free to contact us anytime.',
}: FAQContentProps) {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  const categories = useMemo(() => {
    const cats = ['All', ...Array.from(new Set(faqs.map((f) => f.category)))];
    return cats;
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    return faqs.filter((faq) => {
      const matchCategory =
        activeCategory === 'All' || faq.category === activeCategory;
      const matchSearch =
        searchQuery === '' ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCategory && matchSearch;
    });
  }, [faqs, activeCategory, searchQuery]);

  return (
    <section className="py-24 relative">
      <div className="absolute inset-0 bg-[oklch(0.15_0.01_220)]" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[oklch(0.62_0.19_150)]/10 border border-[oklch(0.62_0.19_150)]/20 text-[oklch(0.72_0.22_150)] text-sm font-medium mb-4">
            {eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {description}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-[oklch(0.62_0.19_150)] text-[oklch(0.12_0.02_150)] shadow-lg shadow-[oklch(0.62_0.19_150)]/30'
                  : 'bg-[oklch(0.20_0.015_220)] text-muted-foreground border border-[oklch(0.28_0.025_170)] hover:text-foreground hover:border-[oklch(0.62_0.19_150)]/40'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-xl mx-auto mb-10">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search questions..."
            className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-[oklch(0.20_0.015_220)] border border-[oklch(0.28_0.025_170)] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-[oklch(0.62_0.19_150)]/60 focus:shadow-[0_0_0_4px_oklch(0.62_0.19_150)_/_0.1] transition-all"
          />
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => (
              <div
                key={faq.id}
                className="card-glow rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                  className="w-full flex items-center justify-between gap-4 p-6 text-left"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex-shrink-0 text-sm font-bold text-[oklch(0.72_0.22_150)]/70">
                      Q{faq.id}
                    </span>
                    <span className="font-semibold text-foreground">
                      {faq.question}
                    </span>
                  </div>
                  {openId === faq.id ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0 text-[oklch(0.72_0.22_150)]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  )}
                </button>
                {openId === faq.id && (
                  <div className="px-6 pb-6 pl-16">
                    <div className="h-px bg-gradient-to-r from-transparent via-[oklch(0.62_0.19_150)]/20 to-transparent mb-4" />
                    <p className="text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No matching questions found. Try a different keyword.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
